import React, { useState, useRef } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { UploadCloud, FileJson, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'

interface ImportStandardModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  targetStandardId?: number
}

interface ParsedSummary {
  standardsCount: number
  chaptersCount: number
  standardNames: string[]
}

export function ImportStandardModal({ open, onOpenChange, targetStandardId }: ImportStandardModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [parsedSummary, setParsedSummary] = useState<ParsedSummary | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = () => {
    setFile(null)
    setParsedSummary(null)
    setParseError(null)
    setIsUploading(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    processFile(selectedFile)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const droppedFile = e.dataTransfer.files?.[0]
    if (!droppedFile) return
    processFile(droppedFile)
  }

  const processFile = (selectedFile: File) => {
    setFile(selectedFile)
    setParseError(null)
    setParsedSummary(null)

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string
        const json = JSON.parse(text)

        let standards: any[] = []
        if (Array.isArray(json)) {
          standards = json
        } else if (typeof json === 'object' && json !== null) {
          if (Array.isArray(json.standards)) {
            standards = json.standards
          } else if (json.standard && typeof json.standard === 'object') {
            standards = [json.standard]
          } else if (json.name) {
            standards = [json]
          }
        }

        if (standards.length === 0) {
          setParseError('No standards data found in the selected JSON file.')
          return
        }

        let chaptersCount = 0
        const standardNames: string[] = []

        standards.forEach((std: any) => {
          if (std.name) standardNames.push(`${std.name} (Sort: ${std.sort_order ?? 0})`)
          const chapters = Array.isArray(std.chapters) ? std.chapters : []
          chaptersCount += chapters.length
        })

        setParsedSummary({
          standardsCount: standards.length,
          chaptersCount,
          standardNames
        })
      } catch (err: any) {
        setParseError(`Invalid JSON format: ${err.message}`)
      }
    }
    reader.readAsText(selectedFile)
  }

  const handleImport = () => {
    if (!file) return

    setIsUploading(true)
    const endpoint = targetStandardId ? `/admin/standards/${targetStandardId}/import` : '/admin/standards/import'

    router.post(endpoint, {
      file: file
    }, {
      forceFormData: true,
      onSuccess: () => {
        setIsUploading(false)
        resetState()
        onOpenChange(false)
      },
      onError: () => {
        setIsUploading(false)
      },
      onFinish: () => {
        setIsUploading(false)
      }
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetState()
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadCloud className="h-5 w-5 text-primary" />
            Import Standards from JSON
          </DialogTitle>
          <DialogDescription>
            Upload a standards JSON export file to import or sync standards and curriculum links. Existing standards are safely merged without duplicates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
            className="hidden"
          />

          <div
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
          >
            <FileJson className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-sm font-medium text-foreground">
              {file ? file.name : 'Click to select or drag & drop JSON file'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Supports single standard or bulk standards JSON exports
            </p>
          </div>

          {parseError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{parseError}</span>
            </div>
          )}

          {parsedSummary && (
            <div className="rounded-md border bg-muted/20 p-3 space-y-2 text-xs">
              <div className="flex items-center gap-1.5 font-medium text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span>Ready to Import:</span>
              </div>
              <div className="grid grid-cols-2 gap-2 py-1 text-center">
                <div className="p-2 rounded bg-background border">
                  <p className="font-bold text-base text-foreground">{parsedSummary.standardsCount}</p>
                  <p className="text-[10px] uppercase font-semibold text-muted-foreground">Standards</p>
                </div>
                <div className="p-2 rounded bg-background border">
                  <p className="font-bold text-base text-foreground">{parsedSummary.chaptersCount}</p>
                  <p className="text-[10px] uppercase font-semibold text-muted-foreground">Linked Chapters</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 pt-1 max-h-20 overflow-y-auto">
                {parsedSummary.standardNames.map((name, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px]">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              resetState()
              onOpenChange(false)
            }}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={!file || !!parseError || !parsedSummary || isUploading}
            className="gap-1.5 font-medium"
          >
            {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isUploading ? 'Importing...' : 'Import Data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
